import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { en } from '../../lib/i18n/en';
import { ISlot } from '../../organisms/bo_calendar_slot/types';
import { ITimeRange, ITimeRangeMinutes } from '../types';
import { getCombinedDateTimeString } from '../../lib/timeUtils';
import { TimeUnit } from '@bondsports/date-time';

dayjs.extend(utc);

export const MIN_HOUR_HEIGHT = 40;
export const MIN_HOUR_WIDTH = 40;
export const DEFAULT_HOUR_HEIGHT = 90;
export const DEFAULT_HOUR_WIDTH = 82;

export enum ETimeFormat {
	TIME_12H_NO_SPACE = 'hh:mmA',
	TIME_24H = 'HH:mm:ss',
	SHORT_DATE = 'ddd, MMM DD',
	FULL_DATE = 'YYYY-MM-DD',
}

export const TIME_FORMAT = 'hh:mm a';
export const HOURS_PER_DAY = 24;
export const MINUTES_PER_HOUR = 60;
export const SECONDS_IN_MINUTE = 60;
export const DEFAULT_NEW_SLOT_DURATION = MINUTES_PER_HOUR;
export const DEFAULT_NEW_SLOT_HOUR_SIZE = DEFAULT_HOUR_HEIGHT;
export const DEFAULT_NEW_SLOT_HORIZONTAL_HOUR_SIZE = DEFAULT_HOUR_WIDTH;
export const DEFAULT_DND_STEP_LENGTH = 5;

export const MINUTES_IN_24_HOURS = HOURS_PER_DAY * MINUTES_PER_HOUR;

export let oneHourHeight = DEFAULT_HOUR_HEIGHT;
export let oneHourWidth = DEFAULT_HOUR_WIDTH;

/** Sets vertical hour size used for layout math. */
export const setNewHourHeight = (height = DEFAULT_HOUR_HEIGHT) => {
	oneHourHeight = Math.max(height, MIN_HOUR_HEIGHT);
};

/** Sets horizontal hour size used for layout math. */
export const setNewHourWidth = (width = DEFAULT_HOUR_WIDTH) => {
	oneHourWidth = Math.max(width, MIN_HOUR_WIDTH);
};

/** Pixel size of one DnD / grid step for the given hour size and step length. */
export const getGridStepSize = (hourSize: number, stepLengthMinutes: number = DEFAULT_DND_STEP_LENGTH): number => {
	const safeStepLength = stepLengthMinutes > 0 ? stepLengthMinutes : DEFAULT_DND_STEP_LENGTH;
	return (hourSize * safeStepLength) / MINUTES_PER_HOUR;
};

export const calculateMarginFromMinutes = (minutes: number) => {
	return (minutes / 60) * oneHourHeight;
};

export const HOUR_GUTTER_OFFSET_PX = 6;

export const calculateMarginFromHours = (h: number) => {
	return h * oneHourHeight + HOUR_GUTTER_OFFSET_PX;
};

export const calculateMinutesFromHeight = (divHeight: number) => {
	return (60 * divHeight) / oneHourHeight;
};

export const calculateMarginLeftFromMinutes = (minutes: number) => {
	return (minutes / 60) * oneHourWidth;
};

export const calculateMarginLeftFromHours = (h: number) => {
	return h * oneHourWidth;
};

export const calculateMinutesFromWidth = (width: number) => {
	return (60 * width) / oneHourWidth;
};

export const getHours = (startTime: string, endTime: string) =>
	dayjs(startTime).format(TIME_FORMAT) + ' - ' + dayjs(endTime).format(TIME_FORMAT);

/** Wall-clock time from minutes since midnight (0–1440). Uses hour/minute fields, not duration from midnight, so DST does not shift calendar rows. */
export const getTimeFromMinutes = (minutes: number): string => {
	const hours: number = Math.floor(minutes / MINUTES_PER_HOUR) % HOURS_PER_DAY;
	const mins: number = minutes % MINUTES_PER_HOUR;
	return dayjs().hour(hours).minute(mins).second(0).format(TIME_FORMAT);
};

/** HH:mm:ss for activity-time comparisons without dayjs (immune to DST duration math). */
export const formatWallClockMinutesAs24H = (minutes: number): string => {
	const hours: number = Math.floor(minutes / MINUTES_PER_HOUR) % HOURS_PER_DAY;
	const mins: number = minutes % MINUTES_PER_HOUR;
	return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
};

export const isDateBetweenDates = (
	dateToVerify: string,
	startDate: string,
	endDate: string,
	format: string = 'DD/MM/YYYY'
): boolean => {
	// startDate <= dateToVerify || dateToVerify <= endDate
	const dateToVerifyFormat = dayjs.utc(dateToVerify).format(format);
	const startDateFormat = dayjs.utc(startDate).format(format);
	const endDateFormat = dayjs.utc(endDate).format(format);

	return (
		(dayjs(dateToVerifyFormat, format).isAfter(dayjs(startDateFormat, format)) ||
			dayjs(dateToVerifyFormat, format).isSame(dayjs(startDateFormat, format))) &&
		(dayjs(endDateFormat, format).isAfter(dayjs(dateToVerifyFormat, format)) ||
			dayjs(endDateFormat, format).isSame(dayjs(dateToVerifyFormat, format)))
	);
};

/*
 * need to understand if it is
 * A. all day or full day -> startDate is start of the day, endDate is end of the day
 * B. endDate is pass midnight -> start Date no change, endDate is set to end of the day
 * C. startDate is befor midnight -> end Date no change, startDate is set to start of the day
 * we need to validate the between the currentDate and (startDate or endDate) has a gap of a day
 */
export const isDayPass = (dateToVerify: string, dateToCompare): boolean => {
	const dateToVerifyStartOfDay = dayjs(dateToVerify).startOf('day');
	const dateToCompareStartOfDay = dayjs(dateToCompare).startOf('day');
	return dateToVerifyStartOfDay.diff(dateToCompareStartOfDay, 'day') > 1;
};

export const diffCalculation = (startDateTime: string, endDateTime: string): string => {
	// Calculate the total difference in minutes
	const dayJsStartDateTime = dayjs.utc(startDateTime);
	const dayJsEndDateTime = dayjs.utc(endDateTime);

	let totalMinutes = dayJsStartDateTime.isBefore(dayJsEndDateTime)
		? dayjs.utc(endDateTime).diff(dayjs.utc(startDateTime), 'minutes')
		: dayjs.utc(startDateTime).diff(dayjs.utc(endDateTime), 'minutes');

	// Define unit conversions
	const minutesInAnHour = 60;
	const minutesInADay = 1440; // 24 * 60
	const minutesInAWeek = 10080; // 7 * 1440

	// Calculate each unit
	const weeks = Math.floor(totalMinutes / minutesInAWeek);
	totalMinutes %= minutesInAWeek;

	const days = Math.floor(totalMinutes / minutesInADay);
	totalMinutes %= minutesInADay;

	const hours = Math.floor(totalMinutes / minutesInAnHour);
	totalMinutes %= minutesInAnHour;

	const minutes = totalMinutes;

	// Construct a human-readable output
	const result: string[] = [];
	if (weeks) result.push(`${weeks} ${en.durationUnitTypes.weeks}`);
	if (days) result.push(`${days} ${en.durationUnitTypes.days}`);
	if (hours) result.push(`${hours} ${en.durationUnitTypes.hours}`);
	if (minutes) result.push(`${minutes} ${en.durationUnitTypes.minutes}`);

	return result.length > 0 ? result.join(' ') : `0 ${en.durationUnitTypes.minutes}`;
};

export const diffCalculationFromDateTime = (
	startDate: string,
	startTime: string,
	endDate: string,
	endTime: string
): string => {
	const startDateTime = getCombinedDateTimeAsString(startDate, startTime);
	const endDateTime = getCombinedDateTimeAsString(endDate, endTime);
	const diff = diffCalculation(startDateTime, endDateTime);
	return diff;
};

export const getCombinedDateTime = (date: string, time: string): Date => {
	const combinedDateTime = dayjs.utc(`${dayjs(date).format('YYYY-MM-DD')}T${time}`, 'YYYY-MM-DDTHH:mm:ss');
	return new Date(combinedDateTime.format('YYYY-MM-DDTHH:mm:ss[Z]'));
};

export const getCombinedDateTimeAsString = (date: string, time: string): string => {
	if (dayjs(time).isValid()) {
		return dayjs(time).format('YYYY-MM-DDTHH:mm:ss.SSS').toString();
	}
	const combinedDateTime = dayjs.utc(`${dayjs(date).format('YYYY-MM-DD')}T${time}`, 'YYYY-MM-DDTHH:mm:ss');
	return combinedDateTime.toString();
};

export const isSameDate = (date: string, dateToCompare: string): boolean => {
	return dayjs.utc(date).isSame(dayjs.utc(dateToCompare), 'day');
};

export const getLowestTime = (time: string, overallTime?: string): string => {
	if (!overallTime) {
		return time;
	}

	return dayjs.utc(`2021-01-01T${time}`).isBefore(dayjs.utc(`2021-01-01T${overallTime}`)) ? time : overallTime;
};

export const getMaxTime = (time: string, overallTime?: string): string => {
	if (!overallTime) {
		return time;
	}

	return dayjs.utc(`2021-01-01T${time}`).isAfter(dayjs.utc(`2021-01-01T${overallTime}`)) ? time : overallTime;
};

export const formatTime = (time: string, outputFormat: ETimeFormat, inputFormat?: ETimeFormat) => {
	return dayjs.utc(time, inputFormat).format(outputFormat);
};

export function timeInMinutes(time: string): number {
	const [hours, minutes, seconds] = time.split(':').map(Number);
	return Math.ceil(hours * MINUTES_PER_HOUR + minutes + seconds / SECONDS_IN_MINUTE);
}

export function convertTimeRangeToMinutes(timeRange: ITimeRange): ITimeRangeMinutes {
	const isMidnight = timeRange.start >= timeRange.end;
	return {
		start: timeInMinutes(timeRange.start),
		// Converts the end time of the time range to minutes and adds an extra day's worth of minutes if the time range spans across midnight.
		end: timeInMinutes(timeRange.end) + MINUTES_IN_24_HOURS * (Number(isMidnight) || 0),
	};
}

/**
 * Checks if a given slot intersects with the current date.
 * @param slot {ISlot} - The slot to check for intersection.
 * @param date {string} - The current date in ISO format (YYYY-MM-DD).
 * @return {boolean} - Returns true if the slot intersects with the current date, otherwise false.
 */
export const checkForIntersectionWithDate = (slot: ISlot, date: string): boolean => {
	const slotStart: string = getCombinedDateTimeString(slot.startDate, slot.startTime);
	const slotEnd: string = getCombinedDateTimeString(slot.endDate, slot.endTime);

	const dateToCheck: dayjs.Dayjs = dayjs.utc(date);
	const endOfDay: dayjs.Dayjs = dateToCheck.endOf(TimeUnit.DAY);
	const startOfDay: dayjs.Dayjs = dateToCheck.startOf(TimeUnit.DAY);

	return dayjs.utc(slotEnd).isAfter(startOfDay) && dayjs.utc(slotStart).isBefore(endOfDay);
};
